import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import ModalPortal from "../sections/common/ModalPortal";
import PrescriptionVerificationService from "../service/PrescriptionVerificationService";
import "../styles/verifyPrescription.scss";

/**
 * "Verify prescription" — the header chip (Figma 3015:4405, "CTA · View all
 * documents" styled, beside the therapy tag) and the dialog it opens (Figma
 * 4471:4105, "Modal · Verify patient").
 *
 * THE FLOW, APIS AND RULES ARE LEGACY'S — bridge's new-bridge
 * PatientTrackingStatus.js ("Verify Patient"); only the UI is the board's:
 *   · the record is read on mount (GET /patients/{id}/verify). With no record
 *     there is nothing to verify, and legacy's button is inert — here the chip
 *     is not drawn at all (Nittin, 2026-09-17: remove it wherever it does not
 *     apply, never show it disabled). The same goes while the read is out and
 *     when it fails: the chip appears only once there is a record to answer;
 *   · three answers: Rx uploaded WITH the client drug, WITHOUT it (status 1 / 2,
 *     each asking whether the doctor matches the code: comment 1 / 2), or a
 *     wrong document (status 3, comment 3);
 *   · the saved answer is pre-selected (legacy's getVerifyStatus, which also
 *     reads an old "status 2, comment 0" as a wrong document);
 *   · the client-drug wording follows the patient's client code, verbatim
 *     (Pfizer's Folvite / Prevenar, Zydus, Daglif / Zyquis …);
 *   · Submit sends { status, commentType } (PUT) and the answer the server
 *     returns becomes the new saved one.
 * Deliberate differences, all about not losing an answer silently: choosing a
 * sub-option also chooses its group (legacy let a hidden comment change), Submit
 * waits for a complete answer, and a failed save keeps the dialog open with the
 * server's message instead of closing it.
 *
 * WHO SEES IT is the host's decision (phr: nutritionists only; bridge: every
 * user) — this component renders for whoever mounts it.
 *
 * This file is identical in phr-frontend and bridge-frontend.
 */

const PFIZER_CODES = ["03XXNA", "03XXND"];

/** Legacy's per-client wording for "the client drug" (PatientTrackingStatus.js). */
const drugLabel = (patient, withDrug) => {
  const p = patient || {};
  const code = p.clientCode;
  const phone = String(p.phoneno == null ? "" : p.phoneno);
  const w = withDrug ? "with" : "without";
  const pfizer = PFIZER_CODES.indexOf(code) !== -1;
  if (code === "03TORR") return `Prescription (Rx) uploaded ${w} client drug`;
  if (pfizer && code === "03XXNA")
    return `Prescription (Rx) uploaded ${w} Folvite/ Folvite Active/ Folvite DHA/ Folvite MB/ Autrin/ Autrin XT`;
  if (pfizer && code === "03XXND" && phone.startsWith("33")) return `Prescription (Rx) uploaded ${w} Prevenar`;
  if (pfizer && code === "03XXND")
    return `Prescription (Rx) uploaded ${w} Folvite/ Folvite Active/ Folvite DHA/ Folvite MB/ Autrin/ Autrin XT`;
  if (code === "03XXLS") return `Prescription (Rx) uploaded ${w} Zydus drug`;
  if (code === "03ZYSA") return `Prescription (Rx) uploaded ${w} Daglif/ Zyquis`;
  return `Prescription (Rx) uploaded ${w} client drug`;
};

/** Legacy's button text for a saved record — the chip's LABEL, as legacy's is
    (getButtonText, PatientTrackingStatus.js). Two deliberate wordings: legacy
    says "Active" for a verified-on-drug record, which reads as a patient
    status next to the other chips, and it capitalises "Verification". */
export const savedStateText = (record, patient) => {
  if (!record) return "";
  const status = Number(record.status);
  const comment = Number(record.comment);
  const p = patient || {};
  const code = p.clientCode;
  const phone = String(p.phoneno == null ? "" : p.phoneno);
  if (status === 0) return "Not verified yet";
  if (status === 1) return comment === 1 ? "Verified" : "Unsuccessful verification";
  if (status === 2) {
    if (comment !== 1) return "Unsuccessful verification";
    if (code === "03XXND" && phone.startsWith("33")) return "Rx verified, not on Prevenar";
    if (code === "03XXNA" || code === "03XXND") return "Rx verified, not on Folvite…";
    if (code === "03XXLS") return "Rx verified, not on Zydus…";
    if (code === "03ZYSA") return "Rx verified, not on Daglif/ Zyquis";
    return "Rx verified, not on client drug";
  }
  if (status === 3) return "Unsuccessful verification";
  return "";
};

/** The saved record as a selection: { status, comment } or nothing chosen. */
export const selectionOf = (record) => {
  if (!record) return { status: 0, comment: 0 };
  const status = Number(record.status);
  const comment = Number(record.comment);
  if (status === 1 || (status === 2 && comment !== 0)) {
    return { status, comment: comment === 1 || comment === 2 ? comment : 0 };
  }
  /* Legacy reads "status 2, comment 0" as the wrong-document answer. */
  if (status === 3 || (status === 2 && comment === 0)) return { status: 3, comment: 3 };
  return { status: 0, comment: 0 };
};

const initialsOf = (name) =>
  String(name || "")
    .replace(/^dr\.?\s+/i, "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join("");

const errorText = (err, fallback) => {
  if (typeof err === "string" && err.trim()) return err.trim();
  if (err && typeof err.message === "string" && err.message.trim()) return err.message.trim();
  return fallback;
};

const Radio = ({ on, size }) => (
  <span className={`vrx-radio${on ? " is-on" : ""}${size === "sm" ? " is-sm" : ""}`} aria-hidden="true">
    {on ? <span className="vrx-radio-dot" /> : null}
  </span>
);
Radio.propTypes = { on: PropTypes.bool, size: PropTypes.oneOf(["sm", "md"]) };

const SUB_OPTIONS = [
  { comment: 1, label: "Have same doctor as per code added" },
  { comment: 2, label: "Doctor is not as per code" },
];

const VerifyDialog = ({ patient, record, onClose, onSaved }) => {
  const [pick, setPick] = useState(() => selectionOf(record));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [imgBroken, setImgBroken] = useState(false);
  const service = useRef(new PrescriptionVerificationService());
  const alive = useRef(true);
  const cardRef = useRef(null);
  useEffect(() => () => { alive.current = false; }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !saving) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, saving]);

  useEffect(() => {
    if (cardRef.current) cardRef.current.focus();
  }, []);

  const doctor = (record && record.doctor) || {};
  const hasDoctor = !!(doctor && (doctor.id || doctor.name));
  const qualifications = [doctor.title, doctor.speciality].map((x) => String(x || "").trim()).filter(Boolean).join(" — ");
  const complete = pick.status === 3 || ((pick.status === 1 || pick.status === 2) && (pick.comment === 1 || pick.comment === 2));

  const choose = (status, comment) => {
    setError("");
    if (status === 3) return setPick({ status: 3, comment: 3 });
    /* Picking a group keeps the doctor answer already given (legacy
       `comment || "1"`), defaulting to "same doctor". */
    setPick((cur) => ({
      status,
      comment: comment || (cur.comment === 1 || cur.comment === 2 ? cur.comment : 1),
    }));
    return undefined;
  };

  const submit = () => {
    if (!complete || saving) return;
    setSaving(true);
    setError("");
    const pid = patient && (patient.id || patient.patientID);
    service.current.update(
      pid,
      pick.status,
      pick.comment,
      (res) => {
        if (!alive.current) return;
        setSaving(false);
        const saved = {
          ...record,
          status: res && res.status != null ? res.status : pick.status,
          comment: res && res.comment != null ? res.comment : pick.comment,
        };
        EventService.emit("showError", { message: "Prescription verification saved", tone: "success" });
        onSaved(saved);
      },
      (err) => {
        if (!alive.current) return;
        setSaving(false);
        setError(errorText(err, "Couldn't save the verification. Please try again."));
      }
    );
  };

  const group = (status) => {
    const on = pick.status === status;
    return (
      <div className={`vrx-group${on ? " is-on" : ""}`} key={status} role="group" aria-label={drugLabel(patient, status === 1)}>
        <button
          type="button"
          role="radio"
          aria-checked={on}
          className="vrx-row"
          disabled={saving}
          onClick={() => choose(status)}
        >
          <Radio on={on} />
          <span className="vrx-row-label">{drugLabel(patient, status === 1)}</span>
        </button>
        <div className="vrx-subs" role="radiogroup" aria-label="Does the doctor match the code?">
          {SUB_OPTIONS.map((o) => {
            const subOn = on && pick.comment === o.comment;
            return (
              <button
                type="button"
                role="radio"
                aria-checked={subOn}
                key={o.comment}
                className={`vrx-sub${subOn ? " is-on" : ""}`}
                disabled={saving}
                onClick={() => choose(status, o.comment)}
              >
                <Radio on={subOn} size="sm" />
                <span className="vrx-sub-label">{o.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <ModalPortal>
      <div
        className="vrx-backdrop"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget && !saving) onClose();
        }}
      >
        <div
          ref={cardRef}
          className="vrx-card"
          role="dialog"
          aria-modal="true"
          aria-labelledby="vrx-title"
          tabIndex={-1}
        >
          <div className="vrx-head">
            <div className="vrx-title-row">
              <h2 className="vrx-title" id="vrx-title">
                Verify prescription
              </h2>
              <button type="button" className="vrx-x" aria-label="Close" disabled={saving} onClick={onClose}>
                <svg viewBox="0 0 28 28" width="28" height="28" fill="none" aria-hidden="true">
                  <path d="M8.5 8.5L19.5 19.5M19.5 8.5L8.5 19.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              </button>
            </div>
            {hasDoctor ? (
              <div className="vrx-doctor" aria-label="Referring doctor">
                {doctor.profile_image && !imgBroken ? (
                  <img className="vrx-photo" src={doctor.profile_image} alt="" onError={() => setImgBroken(true)} />
                ) : (
                  <span className="vrx-photo is-initials" aria-hidden="true">
                    {initialsOf(doctor.name) || "Dr"}
                  </span>
                )}
                <div className="vrx-doctor-text">
                  <div className="vrx-doctor-meta">
                    <p className="vrx-doctor-name">{doctor.name || "Referring doctor"}</p>
                    {qualifications ? <p className="vrx-doctor-quals">{qualifications}</p> : null}
                  </div>
                  {doctor.code ? <p className="vrx-doctor-code">Doctor code: {doctor.code}</p> : null}
                </div>
              </div>
            ) : null}
          </div>

          <div className="vrx-body">
            <p className="vrx-question">What does the uploaded document show?</p>
            {error ? (
              <p className="vrx-error" role="alert">
                {error}
              </p>
            ) : null}
            <div className="vrx-options" role="radiogroup" aria-label="What does the uploaded document show?">
              {group(1)}
              <span className="vrx-divider" aria-hidden="true" />
              {group(2)}
              <span className="vrx-divider" aria-hidden="true" />
              <div className={`vrx-group is-last${pick.status === 3 ? " is-on" : ""}`}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={pick.status === 3}
                  className="vrx-row"
                  disabled={saving}
                  onClick={() => choose(3)}
                >
                  <Radio on={pick.status === 3} />
                  <span className="vrx-row-label">Wrong document</span>
                </button>
              </div>
            </div>
          </div>

          <div className="vrx-foot">
            <button type="button" className="vrx-btn" disabled={saving} onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="vrx-btn is-primary" disabled={!complete || saving} onClick={submit}>
              {saving ? "Submitting…" : "Submit"}
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

VerifyDialog.propTypes = {
  patient: PropTypes.object,
  record: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

/** The header chip, and the dialog behind it. */
const VerifyPrescription = ({ patient }) => {
  const pid = patient && (patient.id || patient.patientID);
  /* The saved record, once read — null while loading, with none, or on a failed read. */
  const [record, setRecord] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    if (!pid) return undefined;
    let alive = true;
    setRecord(null);
    new PrescriptionVerificationService().get(
      pid,
      (res) => {
        if (alive) setRecord(res && res.status != null ? res : null);
      },
      () => {
        if (alive) setRecord(null);
      }
    );
    return () => {
      alive = false;
    };
  }, [pid]);

  useEffect(() => load(), [load]);

  if (!pid || !record) return null;

  return (
    <>
      {/* The LABEL is the saved state once there is one, as legacy's button is:
          the answer submitted in the dialog is what the header then reads. Only
          a pending record (status 0, nothing submitted yet) keeps the call to
          action. The full text stays in the tooltip, since a long state
          ellipsizes (see verifyPrescription.scss). */}
      <button
        type="button"
        className="ph-chip vrx-chip"
        title={`Verify prescription — current: ${savedStateText(record, patient)}`}
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
      >
        {Number(record.status) === 0 ? "Verify prescription" : savedStateText(record, patient)}
      </button>
      {open ? (
        <VerifyDialog
          patient={patient}
          record={record}
          onClose={() => setOpen(false)}
          onSaved={(saved) => {
            setOpen(false);
            setRecord(saved);
          }}
        />
      ) : null}
    </>
  );
};

VerifyPrescription.propTypes = {
  patient: PropTypes.object,
};

export default VerifyPrescription;
